/** Staff correspondence (broadcast messages to taxpayers). Mirrors /api/correspondence. */

export interface CorrespondenceAttachment {
  id: number;
  fileName: string;
  contentType: string | null;
  fileSize: number | null;
  inline: boolean;
  contentId: string | null;
  /** Short-lived download link (presigned). */
  url: string | null;
}

export interface CorrespondenceTemplate {
  id: number;
  name: string;
  subject: string;
  body: string;
  active: boolean;
  lastModified: string;
  updatedBy: string | null;
  attachments: CorrespondenceAttachment[];
}

export interface CorrespondenceTemplateRequest {
  name: string;
  subject: string;
  body: string;
  active: boolean;
  keepAttachmentIds?: number[];
}

export type CorrespondenceAudienceType = 'ALL' | 'ACCOUNTS' | 'FILTER';

export interface CorrespondenceAudience {
  type: CorrespondenceAudienceType;
  accountIds?: number[];
  accountType?: number | null;
  status?: number | null;
  state?: string | null;
  city?: string | null;
  zip?: string | null;
  registeredFrom?: string | null; // yyyy-MM-dd
  registeredTo?: string | null;
}

export interface SendCorrespondenceRequest {
  templateId?: number | null;
  subject: string;
  body: string;
  audience: CorrespondenceAudience;
  templateAttachmentIds?: number[];
  scheduleAt?: string | null; // ISO instant
}

export type CorrespondenceEmailStatus = 'QUEUED' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'FAILED';

export interface CorrespondenceEmail {
  id: number;
  templateName: string | null;
  subject: string;
  audience: string;
  createdAt: string;
  scheduledAt: string | null;
  sentAt: string | null;
  recipientCount: number;
  sentCount: number;
  failedCount: number;
  status: CorrespondenceEmailStatus;
  createdBy: string | null;
  error: string | null;
}

export interface AccountPick {
  id: number;
  name: string;
  email: string | null;
  type: string | null;
  status: string | null;
}

export interface CorrespondenceAlert {
  id: number;
  name: string;
  triggerEvent: string;
  templateName: string;
  templateId: number;
  enabled: boolean;
  lastTriggered?: Date;
}

/** A file picked in the browser but not uploaded yet. */
export interface LocalFile {
  file: File;
  cid?: string;
  previewUrl: string;
}

/** Files travelling with a save/send: kept existing ids + new local files. */
export interface CorrespondenceFiles {
  keepIds: number[];
  attachments: File[];
  inline: LocalFile[];
}

export const ACCOUNT_TYPES = [
  { value: 1, label: 'Individual Property Owner' },
  { value: 2, label: 'Property Management Company' },
  { value: 3, label: 'Bed & Breakfast / Hotel / Motel' },
];

export const ACCOUNT_STATUSES = [
  { value: 1, label: 'Active' },
  { value: 3, label: 'Inactive' },
  { value: 2, label: 'Closed' },
];

/** Replace cid:xyz references with preview/download URLs. */
export function rewriteCids(body: string, urls: Record<string, string>): string {
  let out = body ?? '';
  for (const [cid, url] of Object.entries(urls)) out = out.split(`cid:${cid}`).join(url);
  return out;
}
